import logging from '../logging';
import { Material } from "../domain";
import { streq } from '../text/case-insensitive';
import { zip } from '../control-flow/zip';

const log = logging('material-controllers');

/**
 * This procedure allows us to list materials in domain models and match them
 * to three.js meshes by named slots. Typically the slots are the names of the
 * materials that the model is assigned in Blender, but could be hard coded in
 * a parametric view implementation.
 *
 * The intended usage is something like:
 *
 * ```
 * const
 *     slots = mesh.material.map(m => m.name),
 *     sorted = sortMaterialsBySlot(slots, model.materials);
 * for(let i=0; i < mesh.material.length; i++) {
 *     sorted[i].bind('change', () => {
 *         mesh.material[i] = mfac.makefrom(sorted[i]);
 *     });
 *     // ... etc.
 * }
 *
 * ```
 *
 */
export const sortMaterialsBySlot = (slots: string[], materials: Material[]): Material[] => {

    const
        slotIndices = new Set(slots.keys()),
        materialIndices = new Set(materials.keys()),
        ordering: [slotIndex: number, materialIndex: number][] = [];
    
    // Either of these errors could be benign, so we'll only emit debugging messages
    if(slotIndices.size < materialIndices.size) {
        log.debug("sortMaterialsBySlot() more domain model materials than slots; some slots will keep the model default material", slots, materials);
    } else if(slotIndices.size > materialIndices.size) {
        log.debug("sortMaterialsBySlot() more slots than domain model materials; some domain model materials will be skipped", slots, materials);
    }

    // First we iterate over the indices of the slots and materials arrays.
    // For any domain model Material with a `slot` that is a case-insensitive
    // match to the name of the slot, we make the binding and then remove the
    // indices from the slotIndices and materialIndices sets.
    for(const j of materialIndices) {
        const mat = materials[j];
        if(mat.slot) {
            for(const i of slotIndices) {
                const slot = slots[i];
                if(streq(mat.slot, slot)) {
                    materialIndices.delete(j);
                    slotIndices.delete(i);

                    ordering.push([i, j]);

                    // break out of the loop over the slots
                    break;
                }
            }
        }
    }

    const result = new Array(
        ordering.length + Math.min(slotIndices.size, materialIndices.size));

    for(const [i, j] of ordering) {
        result[i] = materials[j];
    }

    for(const [i, j] of zip(slotIndices, materialIndices)) {
        result[i] = materials[j];
    }

    return result;
}

//if(false) {
//    const
//        slotOrder = 'cba',
//        slots = Array.from(slotOrder),
//        materials = [
//            { slot: 'b', },
//            { slot: 'c', },
//            { slot: 'a', },
//        ].map(d => Material.fromJSON(d)),
//        sorted = sortMaterialsBySlot(slots, materials),
//        joined = sorted.map(m => m.slot).join('');
//    if(joined == slotOrder) {
//        log.temp('SUCCESS');
//    } else {
//        log.error('FAILURE');
//    }
//}
